import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import PropTypes from 'prop-types';

import './MultiplayerRollReveal.css';
import { playSound } from '../../utils/utils';

const PLAYER_COLORS = [
  '#55f6ff',
  '#ff4fd8',
  '#b9ff4f',
  '#ffcf4a',
  '#8d7dff',
  '#ff7657',
  '#4dffad',
  '#ff6fae',
  '#70a5ff',
  '#d9ff70',
  '#ff9d4a',
  '#c56dff',
];

const ROLL_REVEAL_BASE_MS = 900;
// Allow the longest card landing animation to finish, then hold the board.
const ROLL_SETTLE_MS = 620;
const WINNER_PAUSE_MS = 600;
export const RESULT_REVEAL_DELAY_MS = ROLL_SETTLE_MS + WINNER_PAUSE_MS;

const lastRoll = (player) => player.rolls[player.rolls.length - 1];

const randomRollPreview = () => Math.floor(Math.random() * 100);

const makeDisplayValues = (players) =>
  players.reduce((values, player) => {
    values[player.id] = randomRollPreview();
    return values;
  }, {});

export const getRollRevealDuration = (playerCount) => {
  if (!playerCount) return 0;

  const gaps = Math.max(playerCount - 1, 1);
  const stagger = Math.max(1000, Math.min(1000, 2000 / gaps));
  return Math.round(ROLL_REVEAL_BASE_MS + (playerCount - 1) * stagger);
};

const MultiplayerRollReveal = ({
  players,
  onComplete = () => {},
  variant = 'round',
}) => {
  const reduceMotion = !!window.matchMedia?.('(prefers-reduced-motion: reduce)')
    .matches;
  const [displayValues, setDisplayValues] = useState(() =>
    makeDisplayValues(players)
  );
  const [settledIds, setSettledIds] = useState([]);
  const [lastSettledId, setLastSettledId] = useState(null);
  const intervalRef = useRef(null);
  const timeoutsRef = useRef([]);
  const completedRef = useRef(false);
  const settledIdsRef = useRef(new Set());
  const onCompleteRef = useRef(onComplete);

  useEffect(() => {
    onCompleteRef.current = onComplete;
  }, [onComplete]);

  const clearRevealTimers = () => {
    if (intervalRef.current) clearInterval(intervalRef.current);
    timeoutsRef.current.forEach(clearTimeout);
    intervalRef.current = null;
    timeoutsRef.current = [];
  };

  const completeReveal = useCallback(() => {
    if (completedRef.current) return;
    completedRef.current = true;
    onCompleteRef.current();
    playSound('/sounds/relic-obtained.mp3');
    timeoutsRef.current.push(
      setTimeout(() => playSound('/sounds/low-frequency-dice-impact.mp3'), 80)
    );
  }, []);

  const scheduleCompletion = useCallback(() => {
    timeoutsRef.current.push(
      setTimeout(completeReveal, RESULT_REVEAL_DELAY_MS)
    );
  }, [completeReveal]);

  useEffect(() => {
    clearRevealTimers();
    completedRef.current = false;
    settledIdsRef.current = new Set();
    setSettledIds([]);
    setLastSettledId(null);
    setDisplayValues(makeDisplayValues(players));

    if (!players.length) return undefined;

    if (reduceMotion) {
      const allIds = players.map((player) => player.id);
      const finalValues = players.reduce((values, player) => {
        values[player.id] = lastRoll(player);
        return values;
      }, {});
      settledIdsRef.current = new Set(allIds);
      setDisplayValues(finalValues);
      setSettledIds(allIds);
      setLastSettledId(allIds[allIds.length - 1]);
      completeReveal();
      return clearRevealTimers;
    }

    intervalRef.current = setInterval(() => {
      setDisplayValues((currentValues) => {
        const nextValues = { ...currentValues };
        players.forEach((player) => {
          if (!settledIdsRef.current.has(player.id)) {
            nextValues[player.id] = randomRollPreview();
          }
        });
        return nextValues;
      });
    }, 65);

    const duration = getRollRevealDuration(players.length);
    const stagger =
      players.length > 1
        ? (duration - ROLL_REVEAL_BASE_MS) / (players.length - 1)
        : 0;

    timeoutsRef.current = players.map((player, index) =>
      setTimeout(() => {
        settledIdsRef.current.add(player.id);
        setDisplayValues((currentValues) => ({
          ...currentValues,
          [player.id]: lastRoll(player),
        }));
        setSettledIds(Array.from(settledIdsRef.current));
        setLastSettledId(player.id);

        if (index === players.length - 1) {
          clearRevealTimers();
          scheduleCompletion();
        }
      }, ROLL_REVEAL_BASE_MS + index * stagger)
    );

    return clearRevealTimers;
  }, [players, reduceMotion, completeReveal, scheduleCompletion]);

  const settledIdSet = useMemo(() => new Set(settledIds), [settledIds]);
  const settledPlayers = players.filter((player) =>
    settledIdSet.has(player.id)
  );
  const leadingRoll = settledPlayers.length
    ? Math.max(...settledPlayers.map(lastRoll))
    : null;
  const leaderIds = new Set(
    settledPlayers
      .filter((player) => lastRoll(player) === leadingRoll)
      .map((player) => player.id)
  );
  const allSettled = players.length > 0 && settledIds.length === players.length;
  const progress = players.length
    ? Math.round((settledIds.length / players.length) * 100)
    : 0;

  const revealAll = () => {
    clearRevealTimers();
    const allIds = players.map((player) => player.id);
    const finalValues = players.reduce((values, player) => {
      values[player.id] = lastRoll(player);
      return values;
    }, {});
    settledIdsRef.current = new Set(allIds);
    setDisplayValues(finalValues);
    setSettledIds(allIds);
    setLastSettledId(allIds[allIds.length - 1]);
    scheduleCompletion();
  };

  return (
    <section
      className={`multiplayerReveal multiplayerReveal--${variant}`}
      style={{ '--roll-settle-ms': `${ROLL_SETTLE_MS}ms` }}
      aria-label="Player roll reveal"
    >
      <div className="multiplayerReveal__statusRow">
        <div>
          <span className="multiplayerReveal__signal" aria-hidden="true" />
          <span className="multiplayerReveal__statusText">
            {allSettled ? 'RESULT LOCKED' : 'ROLL SIGNAL LIVE'}
          </span>
        </div>
        <span className="multiplayerReveal__counter" aria-live="polite">
          {settledIds.length}/{players.length} LOCKED
        </span>
      </div>

      <div className="multiplayerReveal__progress" aria-hidden="true">
        <div
          className="multiplayerReveal__progressFill"
          style={{ width: `${progress}%` }}
        />
      </div>

      <div className="multiplayerReveal__arena">
        <div className="multiplayerReveal__scanline" aria-hidden="true" />
        <div className="multiplayerReveal__grid">
          {players.map((player, index) => {
            const isSettled = settledIdSet.has(player.id);
            const isLeader = isSettled && leaderIds.has(player.id);
            const isWinner = allSettled && isLeader && leaderIds.size === 1;
            const isNewest = isSettled && lastSettledId === player.id;
            const cardClasses = [
              'rollContestant',
              isSettled ? 'rollContestant--settled' : 'rollContestant--rolling',
              isLeader ? 'rollContestant--leader' : '',
              isWinner ? 'rollContestant--winner' : '',
              isNewest ? 'rollContestant--newest' : '',
            ]
              .filter(Boolean)
              .join(' ');

            return (
              <article
                key={player.id}
                className={cardClasses}
                style={{
                  '--player-accent':
                    PLAYER_COLORS[index % PLAYER_COLORS.length],
                }}
                aria-label={`${player.name}: ${
                  isSettled ? `rolled ${lastRoll(player)}` : 'rolling'
                }`}
                data-testid={`roll-contestant-${player.id}`}
              >
                {isWinner && (
                  <svg
                    className="rollContestant__winnerOrbit"
                    aria-hidden="true"
                  >
                    <rect
                      x="0.5%"
                      y="0.5%"
                      width="99%"
                      height="99%"
                      rx="13"
                      pathLength="100"
                      vectorEffect="non-scaling-stroke"
                    />
                  </svg>
                )}
                <span className="rollContestant__position" aria-hidden="true">
                  P{index + 1}
                </span>
                {isLeader && (
                  <span className="rollContestant__leaderBadge">
                    {isWinner
                      ? 'WINNER'
                      : leaderIds.size > 1
                      ? 'TIED LEAD'
                      : 'LEADER'}
                  </span>
                )}
                <h2 className="rollContestant__name">{player.name}</h2>
                <div className="rollContestant__drum" aria-hidden="true">
                  <span className="rollContestant__ghostNumber rollContestant__ghostNumber--top">
                    {((displayValues[player.id] || 0) + 73) % 100}
                  </span>
                  <strong className="rollContestant__number">
                    {displayValues[player.id]}
                  </strong>
                  <span className="rollContestant__ghostNumber rollContestant__ghostNumber--bottom">
                    {((displayValues[player.id] || 0) + 31) % 100}
                  </span>
                </div>
                <span className="rollContestant__state">
                  {isSettled ? 'LOCKED' : 'SCRAMBLING'}
                </span>
              </article>
            );
          })}
        </div>
      </div>

      {!allSettled && !reduceMotion && (
        <button
          type="button"
          className="multiplayerReveal__skip"
          onClick={revealAll}
        >
          Reveal all
        </button>
      )}
    </section>
  );
};

MultiplayerRollReveal.propTypes = {
  players: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.oneOfType([PropTypes.number, PropTypes.string]).isRequired,
      name: PropTypes.string.isRequired,
      rolls: PropTypes.arrayOf(PropTypes.number).isRequired,
    })
  ).isRequired,
  onComplete: PropTypes.func,
  variant: PropTypes.oneOf(['round', 'tiebreaker']),
};

export default MultiplayerRollReveal;
